import React, { useState, useEffect } from "react";
import {
  Col,
  Row,
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  Modal,
  Upload,
  message,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import API_MANAGER from "../../../API";
import axios from "axios";
import { s3PreUrl } from "../../../constants";
import dayjs from "dayjs";

function AddDocuments({
  addDocument,
  setAddDocument,
  classData,
  conditions,
  idType,
  userId,
  stateTags,
  visaTypeNo,
  medicalAssessmentTypes,
  certifyingAuthority,
  candidateId,
  onDocumentAdded,
}) {
  const [docType, setDocType] = useState("Passport");
  const [fileList, setFileList] = useState([]);
  const [fileListBack, setFileListBack] = useState([]);
  const [countries, setCountries] = useState([]);

  const getCountries = async () => {
    try {
      const response = await API_MANAGER.getPickListTags({
        type: "COUNTRY",
        limit: 2000,
        sort: "name",
      });
      setCountries(response?.data?.data?.tags || []);
    } catch (error) {
      message.error("Something went wrong");
    }
  };

  useEffect(() => {
    getCountries();
  }, []);

  const handleUpload = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };
  const handleBackUpload = ({ fileList: newFileList }) => {
    setFileListBack(newFileList);
  };
  const [otherDetailForm] = Form.useForm();
  const props = {
    name: "file",
    accept: "image/jpg,image/jpeg,.pdf",
    multiple: false,
    customRequest({ onSuccess }) {
      setTimeout(() => {
        onSuccess("ok");
      }, 0);
    },
    onChange(info) {
      const { status } = info.file;
      if (status !== "uploading") {
        console.log(info.file, info.fileList);
      }
      if (status === "done") {
        message.success(`${info.file.name} file uploaded successfully.`);
      } else if (status === "error") {
        message.error(`${info.file.name} file upload failed.`);
      }
    },
    onDrop(e) {
      console.log("Dropped files", e.dataTransfer.files);
    },
  };
  const handleFinish = async (values) => {
    // This function only runs if validation passes
    // If validation fails, onFinishFailed will be called instead
    try {
      // Validate required data for non-medical and non-police clearance documents
      if (docType !== "Medical" && docType !== "Police Clearance" && (!idType || idType.length === 0)) {
        message.error("Document types not loaded. Please try again.");
        return;
      }

      // Validate document upload is mandatory for Police Clearance
      if (docType === "Police Clearance" && (!fileList || fileList.length === 0 || !fileList[0]?.originFileObj)) {
        message.error("Please upload a document for Police Clearance");
        return;
      }

      if (fileList?.length > 0 && fileList[0]?.originFileObj) {
        const dataDocument = {
          extension: fileList[0]?.type.split("/")[1],
          type: "DOCUMENT",
          name: fileList[0].originFileObj.name,
          contentType: fileList[0]?.type,
        };
        const responseDocument = await API_MANAGER.fileUpload(dataDocument);
        if (!responseDocument?.data?.data?.url) {
          throw new Error("Failed to get upload URL for document");
        }
        await axios.put(
          responseDocument?.data?.data?.url,
          fileList[0]?.originFileObj
        );
        values.document = s3PreUrl + responseDocument.data?.data?.key;
      } else {
        values.document = "";
      }
      
      // Upload back document if it exists
      if (fileListBack?.length > 0 && fileListBack[0]?.originFileObj) {
        const dataDocumentBack = {
          extension: fileListBack[0]?.type.split("/")[1],
          type: "DOCUMENT",
          name: fileListBack[0].originFileObj.name,
          contentType: fileListBack[0]?.type,
        };
        const responseDocumentBack = await API_MANAGER.fileUpload(dataDocumentBack);
        if (!responseDocumentBack?.data?.data?.url) {
          throw new Error("Failed to get upload URL for back document");
        }
        await axios.put(
          responseDocumentBack?.data?.data?.url,
          fileListBack[0]?.originFileObj
        );
        values.documentBack = s3PreUrl + responseDocumentBack.data?.data?.key;
      }
      
      // Handle document type validation for non-medical and non-police clearance documents
      let documentTypeId;
      if (docType !== "Medical" && docType !== "Police Clearance") {
        let documentType = idType.filter((e) => {
          if (e.name === docType) return e._id;
        });
        documentTypeId = documentType[0]?._id;
        
        // Validate documentTypeId for non-medical and non-police clearance documents
        if (!documentTypeId) {
          message.error("Document type not found. Please try again.");
          return;
        }
      }
      
      // Handle Medical documents differently
      if (docType === "Medical") {
        // For Medical documents, use the medical API
        const medicalData = {
          userId: userId,
          document: values.document,
          medicalAssessment: values.medicalAssessment,
          issueDate: values.issueDate,
        };
        await API_MANAGER.createCandidateMedical(medicalData);
      } else if (docType === "Police Clearance") {
        // For Police Clearance, use the additional details API
        const issueDate = values.issueDate ? dayjs(values.issueDate).toISOString() : null;
        const policeClearanceData = {
          userId: userId,
          hasPoliceClearance: true,
          policeClearanceDetails: {
            document: values.document,
            certifyingAuthority: values.certifyingAuthority,
            issueDate: issueDate,
          },
        };
        await API_MANAGER.editCandidateAdditionalDetails(policeClearanceData);
        message.success("Successfully added document");
        otherDetailForm.resetFields();
        
        // Close modal first, then refresh data after a delay to ensure API has processed
        // The useEffect in CandidateEdit will also trigger getData() when addDocument changes
        setAddDocument(false);
        
        // Also call callback with delay to ensure data refresh happens after API processing
        if (onDocumentAdded) {
          setTimeout(() => {
            onDocumentAdded();
          }, 300);
        }
        return;
      } else {
        // For other documents, use the regular document API
        // Ensure class and conditions are arrays (for Driver Licence)
        if (docType === "Driver Licence") {
          values.class = Array.isArray(values.class) ? values.class : (values.class ? [values.class] : []);
          values.conditions = Array.isArray(values.conditions) ? values.conditions : (values.conditions ? [values.conditions] : []);
        }
        
        const objData = {
          userId: userId,
          document: values.document,
          documentData: {
            ...values,
          },
          documentType: documentTypeId,
        };
        values.documentData = { ...values };
        values.userId = userId;
        await API_MANAGER.createCandidateDocument(objData);
        
        // If VISA document is created, automatically update citizenship to Non-Australian with VISA details
        if (docType === "VISA" && candidateId) {
          try {
            const visaData = {
              isAustralian: false,
              visaDetails: {
                visaTypeNo: values.visaTypeNo,
                visaDocument: values.document,
                grantNo: values.grantNo,
                issueDate: values.issueDate ? dayjs(values.issueDate).toISOString() : null,
                expiryDate: values.expiryDate ? dayjs(values.expiryDate).toISOString() : null,
              }
            };
            await API_MANAGER.editCandidateVisa(
              visaData,
              candidateId
            );
          } catch (error) {
            console.error("Error updating citizenship status:", error);
            // Don't fail the document creation if citizenship update fails
          }
        }
      }
      message.success("Successfully added document");
      otherDetailForm.resetFields();
      setAddDocument(false);
      
      // Call callback to refresh data if provided
      if (onDocumentAdded) {
        setTimeout(() => {
          onDocumentAdded();
        }, 100);
      }
    } catch (error) {
      console.error("Error creating document:", error);
      const errorMessage = error?.response?.data?.message?.message || 
                          error?.response?.data?.message || 
                          error?.message || 
                          "An error occurred while creating the document";
      message.error(errorMessage);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log('Form validation failed:', errorInfo);
    console.log('Error fields:', errorInfo.errorFields);
    // The validation messages will automatically appear below the fields
    // No API call will be made
    message.error('Please fill in all mandatory fields');
  };
  return (
    <Modal
      centered
      footer={false}
      open={addDocument}
      title={"Add Document"}
      className="resumeModal"
      onCancel={() => setAddDocument(false)}
    >
      <Form 
        form={otherDetailForm} 
        layout="vertical" 
        requiredMark={false}
        onFinish={handleFinish}
        onFinishFailed={onFinishFailed}
        validateTrigger="onSubmit"
      >
        <Row>
          <Col xs={24}>
            <Row>
              <Col xs={12}>
                <Form.Item name="docType">
                  <Select
                    placeholder="Select Id"
                    defaultValue={"Passport"}
                    onChange={(e) => {
                      setDocType(e);
                      // Trigger document validation when type changes
                      setTimeout(() => {
                        otherDetailForm.validateFields(['document']);
                      }, 100);
                    }}
                  >
                    <Select.Option value="Passport">Passport</Select.Option>
                    <Select.Option value="Driver Licence">
                      Driver Licence
                    </Select.Option>
                    <Select.Option value="Proof of Age/Photo Card">
                      Proof of Age/Photo Card
                    </Select.Option>
                    <Select.Option value="Medical">Medical</Select.Option>
                    <Select.Option value="Police Clearance">Police Clearance</Select.Option>
                    <Select.Option value="VISA">Visa</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col xs={24} style={{ margin: "10px 0px 20px 0px" }}>
                <Form.Item
                  name="document"
                  label={docType === "Police Clearance" || docType === "Medical" ? "Upload Document*" : "Upload Document"}
                  rules={[
                    {
                      validator: () => {
                        if ((docType === "Police Clearance" || docType === "Medical") && (!fileList || fileList.length === 0 || !fileList[0]?.originFileObj)) {
                          return Promise.reject(new Error("Please upload a document"));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  required={docType === "Police Clearance" || docType === "Medical"}
                  validateTrigger="onSubmit"
                >
                  <Upload {...props} onChange={(e) => {
                    handleUpload(e);
                    // Trigger validation when file is uploaded
                    if (docType === "Police Clearance" || docType === "Medical") {
                      setTimeout(() => {
                        otherDetailForm.validateFields(['document']);
                      }, 100);
                    }
                  }}>
                    <Button icon={<UploadOutlined />}>Upload</Button>
                  </Upload>
                </Form.Item>
              </Col>
            </Row>
            {(docType === "Driver Licence" || docType === "Proof of Age/Photo Card") && (
              <Row>
                <Col xs={24} style={{ margin: "10px 0px 20px 0px" }}>
                  <Upload {...props} onChange={(e) => handleBackUpload(e)}>
                    <Button icon={<UploadOutlined />}>Upload Back Document</Button>
                  </Upload>
                </Col>
              </Row>
            )}
            <Row gutter={24}>
              <Col xs={12}>
                {docType === "VISA" && (
                  <Form.Item 
                    name="visaTypeNo" 
                    label="Visa Type No*"
                    rules={[
                      {
                        required: true,
                        message: "Please select visa type",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Visa Type No"
                      showSearch
                      filterOption={(input, option) =>
                        option.props.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {visaTypeNo && Array.isArray(visaTypeNo) && visaTypeNo.length > 0 ? (
                        visaTypeNo.map((e, key) => {
                          return (
                            <Select.Option key={key} value={e?._id || e?.id}>
                              {e?.name}
                            </Select.Option>
                          );
                        })
                      ) : (
                        <Select.Option disabled value="no-data">
                          No visa types available
                        </Select.Option>
                      )}
                    </Select>
                  </Form.Item>
                )}
                {docType === "Passport" && (
                  <Form.Item 
                    name="passportNumber" 
                    label="Passport Number*"
                    rules={[
                      {
                        required: true,
                        message: "Please enter passport number",
                      },
                    ]}
                  >
                    <Input placeholder="Passport number" />
                  </Form.Item>
                )}
                {docType === "Driver Licence" && (
                  <Form.Item 
                    name="licenseNumber" 
                    label="License Number*"
                    rules={[
                      {
                        required: true,
                        message: "Please enter license number",
                      },
                    ]}
                  >
                    <Input placeholder="Licence number" />
                  </Form.Item>
                )}
                {docType === "Proof of Age/Photo Card" && (
                  <Form.Item 
                    name="idNumber" 
                    label="ID Number*"
                    rules={[
                      {
                        required: true,
                        message: "Please enter ID number",
                      },
                    ]}
                  >
                    <Input placeholder="Id number" />
                  </Form.Item>
                )}
                {docType === "Medical" && (
                  <Form.Item 
                    name="medicalAssessment" 
                    label="Medical Assessment*"
                    rules={[
                      {
                        required: true,
                        message: "Please select verification type",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select Verification Type"
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {medicalAssessmentTypes?.map((e, key) => {
                        return (
                          <Select.Option key={key} value={e.id}>
                            {e.name}
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                )}
                {docType === "Police Clearance" && (
                  <Form.Item 
                    name="certifyingAuthority" 
                    label="Certifying Authority*"
                    rules={[
                      {
                        required: true,
                        message: "Please select certifying authority",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select Certifying Authority"
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {certifyingAuthority?.map((e, key) => {
                        return (
                          <Select.Option key={key} value={e.id || e._id}>
                            {e.name}
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                )}
              </Col>
              <Col xs={12}>
                {docType === "VISA" ? (
                  <Form.Item 
                    name="grantNo" 
                    label="Grant No*"
                    rules={[
                      {
                        required: true,
                        message: "Please enter grant number",
                      },
                    ]}
                  >
                    <Input placeholder="Grant No" />
                  </Form.Item>
                ) : docType === "Passport" ? (
                  <Form.Item 
                    name="nationality" 
                    label="Nationality*"
                    rules={[
                      {
                        required: true,
                        message: "Please select nationality",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select Nationality"
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {countries?.map((e, key) => {
                        return (
                          <Select.Option key={key} value={e.name}>
                            {e.name}
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                ) : docType === "Driver Licence" ? (
                  <Form.Item 
                    name="state" 
                    label="State*"
                    rules={[
                      {
                        required: true,
                        message: "Please select state",
                      },
                    ]}
                  >
                    <Select  showSearch
                        filterOption={(input, option) =>
                          option.props.children
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }>
                      {stateTags?.map((e, key) => {
                        return (
                          <Select.Option key={key} value={e?.name}>
                            {e?.name}
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                ) : docType === "Medical" ? (
                  <Form.Item 
                    label="Issue Date*" 
                    name="issueDate"
                    rules={[
                      {
                        required: true,
                        message: "Please select issue date",
                      },
                    ]}
                  >
                    <DatePicker
                      className="w-100"
                      format={"DD-MM-YYYY"}
                      placeholder="Select issue date"
                    />
                  </Form.Item>
                ) : docType === "Police Clearance" ? null : (
                  <Form.Item 
                    name="state" 
                    label="State*"
                    rules={[
                      {
                        required: true,
                        message: "Please select state",
                      },
                    ]}
                  >
                    <Select  showSearch
                        filterOption={(input, option) =>
                          option.props.children
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }>
                      {stateTags?.map((e, key) => {
                        return (
                          <Select.Option key={key} value={e?.id}>
                            {e?.name}
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                )}
              </Col>
            </Row>
            {docType === "Driver Licence" && (
              <Row gutter={24}>
                <Col xs={12}>
                  <Form.Item 
                    name="class" 
                    label="Class*"
                    requiredMark={false}
                    rules={[
                      {
                        required: true,
                        message: "Please select at least one class",
                      },
                    ]}
                  >
                    <Select 
                      className="" 
                      placeholder="Class"
                      mode="multiple"
                      maxTagCount="responsive"
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }>
                      {classData?.map((e, key) => {
                        return (
                          <Select.Option key={key} value={e?.name}>
                            {e?.name}
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item name="conditions" label="Conditions">
                    <Select 
                      className="" 
                      placeholder="Conditions"
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }>
                      {conditions?.map((e, key) => {
                        return (
                          <Select.Option key={key} value={e?.name}>
                            {e?.name}
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            )}
            <Row gutter={24}>
              {(docType === "VISA" || docType === "Police Clearance") && (
                <Col xs={12}>
                  <Form.Item 
                    label={docType === "Police Clearance" ? "Issue Date*" : "Issue Date"} 
                    name="issueDate"
                    dependencies={docType === "Police Clearance" ? [] : ['expiryDate']}
                    rules={
                      docType === "Police Clearance" ? [
                        {
                          required: true,
                          message: "Please select issue date",
                        },
                      ] : [
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const expiryDate = getFieldValue('expiryDate');
                            if (!value && !expiryDate) {
                              return Promise.reject(new Error('Please select at least issue date or expiry date'));
                            }
                            return Promise.resolve();
                          },
                        }),
                      ]
                    }
                  >
                    <DatePicker
                      className="w-100"
                      format={"DD-MM-YYYY"}
                      placeholder="Select issue date"
                    />
                  </Form.Item>
                </Col>
              )}
              {docType !== "Police Clearance" && docType !== "Medical" && (
                <Col xs={12}>
                  <Form.Item 
                    label="Expiry" 
                    name="expiryDate"
                    dependencies={docType === "VISA" ? ['issueDate'] : []}
                    rules={[
                      docType === "VISA" ? ({ getFieldValue }) => ({
                        validator(_, value) {
                          const issueDate = getFieldValue('issueDate');
                          if (!value && !issueDate) {
                            return Promise.reject(new Error('Please select at least issue date or expiry date'));
                          }
                          return Promise.resolve();
                        },
                      }) : {
                        required: true,
                        message: "Please select expiry date",
                      },
                    ]}
                  >
                    <DatePicker
                      className="w-100"
                      format={"DD-MM-YYYY"}
                      placeholder="Select expiry date"
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>
          </Col>
        </Row>
        <Row>
          <Col xs={24} className="buttonCol">
            <Button className="btn1" htmlType="submit">
              Create
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
export default AddDocuments;
